import { C1Alert } from 'c1ui'
import { getOutcomeText, OutcomeIcon } from 'components/core/task/outcome'
import { getShortString } from 'components/core/time/display'
import { TaskValue } from 'lib/actions/task'
import { CertifyOutcome, GrantOutcome, RevokeOutcome } from 'pbts/c1/api/task/v1/task'
import { Timestamp as pbtsTimestamp } from 'pbts/google/protobuf/timestamp'
import { IntlShape, useIntl } from 'react-intl'
import { OutcomeBannerStyles } from './styles'

const getOutcomeTextLong = (
    task: TaskValue,
    outcome: CertifyOutcome | GrantOutcome | RevokeOutcome,
    outcomeTime: pbtsTimestamp,
    intl: IntlShape,
): string => {
    switch (outcome) {
        case CertifyOutcome.CERTIFIED:
        case GrantOutcome.GRANTED:
        case RevokeOutcome.REVOKED:
        case CertifyOutcome.DECERTIFIED:
        case GrantOutcome.DENIED:
        case RevokeOutcome.DENIED:
        case CertifyOutcome.CANCELLED:
        case GrantOutcome.CANCELLED:
        case RevokeOutcome.CANCELLED:
        case GrantOutcome.WAIT_TIMED_OUT:
        case RevokeOutcome.WAIT_TIMED_OUT:
        case CertifyOutcome.WAIT_TIMED_OUT:
            return intl.formatMessage(
                {
                    id: 'task_outcome_approved_long',
                    defaultMessage: 'This task was {outcome} on {date}.',
                },
                {
                    outcome: getOutcomeText(task, intl).toLowerCase(),
                    date: getShortString({ d: pbtsTimestamp.toDate(outcomeTime), variant: 'dense' }),
                },
            )
        case CertifyOutcome.ERROR:
        case GrantOutcome.ERROR:
        case RevokeOutcome.ERROR:
            return intl.formatMessage({
                id: 'task_outcome_error',
                defaultMessage: 'This task has an error',
            })
        case CertifyOutcome.UNSPECIFIED:
        case GrantOutcome.UNSPECIFIED:
        case RevokeOutcome.UNSPECIFIED:
        default:
            return intl.formatMessage({
                id: 'task_outcome_pending_long',
                defaultMessage: 'This task is pending',
            })
    }
}

export const OutcomeBanner = ({ task }: { task: TaskValue }): React.ReactElement | null => {
    const intl = useIntl()
    const typ = task?.type?.taskType
    let outcome
    let outcomeTime

    switch (typ?.oneofKind) {
        case 'certify':
            outcome = typ.certify.outcome
            outcomeTime = typ.certify.outcomeTime
            break
        case 'grant':
            outcome = typ.grant.outcome
            outcomeTime = typ.grant.outcomeTime
            break
        case 'revoke':
            outcome = typ.revoke.outcome
            outcomeTime = typ.revoke.outcomeTime
            break
        default:
            return null
    }

    if (
        outcome === CertifyOutcome.UNSPECIFIED ||
        outcome === GrantOutcome.UNSPECIFIED ||
        outcome === RevokeOutcome.UNSPECIFIED
    ) {
        return null
    }

    return (
        <C1Alert
            severity={outcome === CertifyOutcome.ERROR || outcome === GrantOutcome.ERROR || outcome === RevokeOutcome.ERROR ? 'error' : 'info'}
            icon={<OutcomeIcon outcome={outcome} />}
            sx={OutcomeBannerStyles.container}
            key={`outcome-banner-${outcome}`}
        >
            {getOutcomeTextLong(task, outcome, outcomeTime, intl)}
        </C1Alert>
    )
}
